import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronDown, Users } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useWorker } from "../context/WorkerContext";
import { ToastContainer, toast } from "react-toastify";
import shiftsIcon from "../assets/shiftIcon.png";
import hoursIcon from "../assets/hoursIcon.png";
import companyIcon from "../assets/companyIcon.png";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Side-by-side stat, bigger icon, no background box around it
const IconStat = ({ iconSrc, label, value }) => (
  <div className="flex-1 flex items-center gap-2.5 bg-white rounded-xl px-3 py-3">
    <img src={iconSrc} alt="" className="w-8 h-8 object-contain flex-shrink-0" />
    <div>
      <p className="text-base font-bold text-gray-900 leading-tight">{value}</p>
      <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
    </div>
  </div>
);

const WorkSummaryPage = () => {
  const { token } = useWorker();
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState([]);
  const [userStats, setUserStats] = useState({
    totalWorkingHours: 0,
    totalShifts: 0,
    totalGrossAmount: 0,
    totalDeductedAmount: 0,
    totalIncentive: 0,
    totalReferral: 0,
    totalNetPayment: 0,
    totalEarnings: 0, // Added this field
  });
  const [loading, setLoading] = useState(true);

  const [earningsExpanded, setEarningsExpanded] = useState(false);
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());

  useEffect(() => {
    fetchPaymentSummary();
    fetchUserStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPaymentSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/lenskart/userPaymentSummary`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSummaryData(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch payment summary");
      }
    } catch (error) {
      console.error("Error fetching payment summary:", error);
      toast.error("Failed to load payment details");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/lenskart/userStats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.success) {
        const stats = response.data.data;
        setUserStats({
          totalWorkingHours: stats.totalWorkingHours ?? 0,
          totalShifts: stats.totalShifts ?? 0,
          totalGrossAmount: stats.totalGrossAmount ?? 0,
          totalDeductedAmount: stats.totalDeductedAmount ?? 0,
          totalIncentive: stats.totalIncentive ?? 0,
          totalReferral: stats.totalReferral ?? 0,
          totalNetPayment: stats.totalNetPayment ?? 0,
          totalEarnings: stats.totalEarnings ?? 0, // Store totalEarnings from API
        });
      }
    } catch (error) {
      console.error("Failed to fetch user stats:", error);
    }
  };

  const calculateOverallTotals = () => {
    const totals = { hours: 0, grossAmount: 0, shifts: 0, companies: summaryData.length };
    summaryData.forEach((company) => {
      totals.hours += company.companyTotalHours;
      totals.grossAmount += company.companyTotalGrossAmount;
      totals.shifts += company.companyTotalShifts;
    });
    return totals;
  };

  const overallTotals = calculateOverallTotals();

  const calculatedTotalEarnings = 
    overallTotals.grossAmount +
    userStats.totalIncentive +
    userStats.totalReferral -
    userStats.totalDeductedAmount;

  const totalEarnings = 
    userStats.totalEarnings !== undefined && userStats.totalEarnings !== null && userStats.totalEarnings !== 0
      ? userStats.totalEarnings
      : calculatedTotalEarnings;

  const netEarnings = 
    overallTotals.grossAmount +
    userStats.totalIncentive +
    userStats.totalReferral -
    userStats.totalDeductedAmount;

  const toggleCompany = (companyId) => {
    setExpandedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  return (
    <div className="bg-white min-h-screen">
      <ToastContainer position="top-center" autoClose={3000} />

      {/* Header - border removed, less bottom padding */}
      <div className="top-0 z-20 bg-white flex items-center justify-center relative px-4 py-4 pt-20">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-4 p-1 -ml-1 text-gray-900 hover:text-gray-600 transition-colors"
        >
          <ChevronLeft className="h-7 w-7" strokeWidth={2.5} />
        </button>
        <h1 className="text-xl font-bold text-gray-900">Work Summary</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading payment details...</p>
          </div>
        </div>
      ) : summaryData.length === 0 ? (
        <div className="text-center py-16 px-4">
          <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-indigo-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment Data</h3>
          <p className="text-gray-600">You haven't completed any shifts yet.</p>
        </div>
      ) : (
        <div className="px-4 pt-2 pb-10">
          {/* Overall Summary */}
          <div className="bg-[#E4E3FB] rounded-2xl p-5 mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Overall Summary</h3>

            {/* Shift Hours / Total Shifts - side by side, bigger icons, no icon box */}
            <div className="flex gap-3 mb-4">
              <IconStat iconSrc={hoursIcon} label="Shifts Hours" value={overallTotals.hours.toFixed(2)} />
              <IconStat iconSrc={shiftsIcon} label="Total Shifts" value={overallTotals.shifts} />
            </div>

            {/* Total Earnings - collapsed by default, expands to show breakup */}
            <div className="bg-white rounded-xl overflow-hidden">
              <button
                onClick={() => setEarningsExpanded((prev) => !prev)}
                className="w-full flex items-center justify-between px-4 py-3.5"
              >
                <span className="text-sm text-gray-700">Total Earnings</span>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold text-gray-900">₹{totalEarnings}</span>
                  <ChevronDown
                    className={`h-4 w-4 text-gray-500 transition-transform ${
                      earningsExpanded ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>

              {earningsExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-gray-100">
                  <div className="space-y-2.5 mt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Wage Earnings</span>
                      <span className="text-xs font-semibold text-gray-900">₹{userStats.totalGrossAmount}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Incentive Earnings</span>
                      <span className="text-xs font-semibold text-green-600">
                        + ₹{userStats.totalIncentive}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Referral Earnings</span>
                      <span className="text-xs font-semibold text-green-600">
                        + ₹{userStats.totalReferral}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Deductions</span>
                      <span className="text-xs font-semibold text-red-500">
                        - ₹{userStats.totalDeductedAmount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200 border-t-2">
                      <span className="text-sm font-semibold text-green-700">Net Earnings</span>
                      <span className="text-sm font-bold text-green-600">₹{netEarnings}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Companies section header */}
          <div className="flex items-center gap-2 mb-4">
            <img src={companyIcon} alt="" className="w-5 h-5 object-contain" />
            <h3 className="text-lg font-bold text-gray-900">Companies</h3>
            <span className="text-xs font-semibold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
              {overallTotals.companies}
            </span>
          </div>

          {/* Company cards - collapsed by default */}
          <div className="space-y-4">
            {summaryData.map((company) => {
              const isExpanded = expandedCompanies.has(company.companyId);
              return (
                <div
                  key={company.companyId}
                  className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
                >
                  <button
                    onClick={() => toggleCompany(company.companyId)}
                    className="w-full flex items-center gap-3 px-4 py-4"
                  >
                    {company.companyLogo ? (
                      <img
                        src={company.companyLogo}
                        alt={company.companyName}
                        className="w-9 h-9 rounded-full object-contain border border-gray-200 bg-white p-1 flex-shrink-0"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : (
                      <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center border border-gray-200 flex-shrink-0">
                        <Users className="h-5 w-5 text-indigo-600" />
                      </div>
                    )}

                    <div className="flex-1 text-left">
                      <h4 className="font-bold text-gray-900 text-base">{company.companyName}</h4>
                      <p className="text-xs text-gray-500">
                        {company.companyTotalShifts} shifts · {company.companyTotalHours.toFixed(2)} hrs
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-base font-bold text-green-600">
                        ₹{company.companyTotalGrossAmount}
                      </p>
                    </div>

                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isExpanded && (
                    <div className="border-t border-gray-100">
                      <div className="bg-[#E7F7EC] px-4 py-4">
                        <div className="grid grid-cols-3 gap-2">
                          <div className="text-center">
                            <p className="text-lg font-bold text-gray-900">
                              {company.companyTotalHours.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-600">Total Hours</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-gray-900">
                              {company.companyTotalShifts}
                            </p>
                            <p className="text-xs text-gray-600">Total Shifts</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-green-600">
                              ₹{company.companyTotalGrossAmount}
                            </p>
                            <p className="text-xs text-gray-600">Gross Earnings</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white">
                        {company.departments.map((dept, deptIndex) => (
                          <div key={deptIndex}>
                            <div className="bg-gray-200 px-4 py-2.5 text-center">
                              <p className="text-sm font-semibold text-gray-800">
                                {dept.department || "N/A"}
                              </p>
                            </div>
                            <div className="px-4 py-4">
                              <div className="grid grid-cols-3 gap-2">
                                <div className="text-center">
                                  <p className="text-lg font-bold text-gray-900">
                                    {dept.totalWorkingHours.toFixed(2)}
                                  </p>
                                  <p className="text-xs text-gray-500">Total Hours</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-lg font-bold text-gray-900">
                                    {dept.totalShifts}
                                  </p>
                                  <p className="text-xs text-gray-500">Total Shifts</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-lg font-bold text-green-600">
                                    ₹{dept.totalGrossAmount}
                                  </p>
                                  <p className="text-xs text-gray-500">Gross Earnings</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkSummaryPage;